import React from 'react';
import { logo } from '../Static.tsx';
import { makeExecuteRequest, sendExecuteRequest } from '../Socket.tsx';

const HomeButton: React.FC<{}> = () => {
  return (<div className='clicky' onClick={() => (document.location.href = '#')}>
    <div className="logo-container" style={{ display: 'flex', alignItems: 'center' }}>
      <img
        src={logo}
        alt="Logo"
        className="startup-logo"
        width={"80px"}
      />
      <h2 style={{ margin: 0, marginLeft: "10px" }}>Playground DB </h2>
    </div>
  </div>);
}

export const HomePage: React.FC<{}> = () => {
    return (
        <div className="App" style={{ paddingBottom: '32px' }}>
            <HomeButton />
            <header className='App-header'>
                <h1>Playground DB</h1>
                <h4>Build a SQL database in the Rust Playground</h4>
                <div style={{ padding: "5px", color: 'black', fontSize: 15, textAlign: 'left' }}>
                    <h3>
                        Neil Lugovoy - <a style={{ fontWeight: 'bold' }} href="https://truesearcher.com">True Searcher</a>
                    </h3>
                </div>
            </header>
            <div
                style={{
                    textAlign: 'left',
                    fontSize: '1.2em',           // base font size
                    background: "#f5f5f5",
                    color: 'black',
                    borderRadius: "5px",
                    border: "1px solid black",   // fixed border syntax
                    width: '75%',
                    padding: '16px',
                }}
            >
                <h3>About</h3>
                <div className='selector-of-that-element'>
                    <p>
                        I've been working on a search engine that plans queries across multiple databases. 
                        I felt hampered by my lack of knowledge of databases. 
                        How do query planners work? How should I manage data consistency across
                        multiple databases? 
                    </p>
                    <p>
                        I learn from doing, so I built a database from scratch and did a lot of research. 
                        I put together this guide in my free time.
                    </p>
                    <p>
                        I hope you enjoy :)
                    </p>
                </div>
                <h3>References</h3>
                <div className='selector-of-that-element'>
                    <p>
                        Databases is a huge field full of very smart people who have worked on hard problems for a very long time.
                        I try to reference authoritative documents and quote people who are more knowledgeable than me. If I'm speaking my own opinion, 
                        I'll make that clear.
                    </p>
                    <p>
                        Please feel free to send any corrections to me.
                    </p>
                </div>
                <h3>How the code works</h3>
                <p>All of the Rust code runs in the Rust Playground.</p>
                <p>You can run it from the browser here, or you're welcome to clone the <a href="https://github.com/neil-ski/playground_db">repo</a> and work on it on your laptop.</p>
                <p>Each code editing window views/edits the same file.</p>
                <p>If you want to work on a single part, you can use my solution from the other part so it will run.</p>
                <h3>Code structure</h3>
                <p>
                    To run in the Rust Playground, it must be one giant file. So the 8 files get appended when it is run via the browser. 
                </p>
                <p>
                    I've written the code such that you will never have to go back and change a previous subpart.
                    Rather than making the tests separate files, they are written in line after the subpart containing the required functions.
                </p>
                <p>
                    I've avoided explicit lifetimes as much as possible. I feel they complicate the code too much and distract from the database concepts.
                </p>
                <p>
                    In this way, it's not perfectly idiomatic Rust code but I try to introduce the following topics: Enums, type discrimination, New Types, Async, Mutexes, Atomics.
                </p>
                <h3>Surprises</h3>
                    
                    <p>
                        I was surprised by how complicated a disk scheduler could be. 
                        I hadn't appreciated how much re-ordering of disk reads/writes 
                        it can do. I was surprised by how complicated MySQL's WAL group-commit
                        implementation is.
                    </p>
                    <p>
                        Conceptually a buffer pool is pretty simple: a contiguous aligned chunk of 
                        memory with fixed-sizes pages that are guarded by read/write mutexes. Implementing that 
                        in Rust is not so straightforward. You have to convince the compiler to let you do 
                        all of those things. As far as I can tell, the only way to do this is with the <code>unsafe</code> keyword.
                    </p>
                    <p>
                        I was surprised by how latches are separated from locks conceptually. I was also surprised by how deadlocks 
                        with latches vs locks are handled differently.
                        There's a lot of tricks about acquiring latches in the same order in your B+ tree. 
                    </p>
                    <p>
                        I had not appreciated that a rules-based optimizer is different from a cost-based optimizer and how much easier the former is. 
                    </p>
                    <p>
                        I had not appreciated how much multi-version concurrency control interacts with all parts of your system. I was especially surprised how
                        old keys can stick around in your B+ tree if you don't implement compaction of old versions. Compaction adds a lot of complexity, without it,
                        it's a lot easier to make things correct. 
                    </p>
                
                <h3>Sections</h3>
                <p>
                    I'm embarking on the process of converting my code into a format that works well for the small sections I discussed above.
                </p>
                <ol>
                    <li>
                        <a href={"#/1"}>
                            Part 1 - Serialization
                        </a>
                    </li>
                    <li>
                        Part 2 - Disk Layout & Scheduler
                    </li>
                    <li>
                        Part 3 - Buffer Pool
                    </li>
                    <li>
                        Part 4 - Concurrent B+ Tree Index
                    </li>
                    <li>
                        Part 5 - Query Execution Engine
                    </li>
                    <li>
                        Part 6 - Query Optimizer
                    </li>
                    <li>
                        Part 7 - Transactions - Multi Version Concurrency Control
                    </li>
                    <li>
                        Part 8 - Transactions - Fault Tolerance & Recovery
                    </li>
                </ol>
            </div>
        </div>
    );
}
